import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useIncidentsAgGridColumns, useIncidentsCountByStatus, useIncidentsMain } from '../../hooks';
import { Incident } from '../../models/Incident';
import { IncidentsAgGridTable, IncidentsCardList } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection, IncidentStatusToolbar } from '../interaction';
import { ActionsModal, ActionsModalRef, HistoryModal, HistoryModalRef } from '../modals';
import { useCouriers } from '../../../../Mappings/List/hooks/useMappings';

export const Main = () => {
  const { t } = useTranslation('incidentsModule');
  const actionsModalRef = useRef<ActionsModalRef>(null);
  const historyModalRef = useRef<HistoryModalRef>(null);
  const { data: couriers = [] } = useCouriers();
  const couriersMap = useMemo(() => {
    const map = new Map<number, (typeof couriers)[number]>();
    couriers.forEach((c) => map.set(c.id, c));
    return map;
  }, [couriers]);

  const {
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
    sorting,
    selectedColumns,
    incidentsViewProps,
    hasActiveFilters,
    fixedColumns,
    filterConditions,
    searchTerm,
    shopIds,
    teamIds,
    courierIds,
    handleFilterChange,
    handleSearch,
    handleShopIdsChange,
    handleTeamIdsChange,
    handleColumnsChange,
    handleSortingChange,
    activeStatusTab,
    handleStatusTabChange,
    queryFiltersBaseForStatusCounts,
    handleCourierChange,
    handleClearAll,
  } = useIncidentsMain();

  const { counts: statusCounts, isLoadingCounts } = useIncidentsCountByStatus(queryFiltersBaseForStatusCounts);

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const handleOpenActions = useCallback(
    (incident: Incident) => {
      // Llamar directamente sin verificar dependencias
      if (actionsModalRef.current) {
        actionsModalRef.current.openWithIncident(incident);
      }
    },
    [], // Sin dependencias para evitar recreación
  );

  const handleOpenHistory = useCallback((incident: Incident) => {
    historyModalRef.current?.openWithIncident(incident);
  }, []);

  const handleSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const { allColumnDefs, filteredColumnDefs } = useIncidentsAgGridColumns({
    selectedColumns,
    onOpenActions: handleOpenActions,
    onOpenHistory: handleOpenHistory,
  });

  // Convertir columnas AG Grid a formato para ColumnSelector
  // Usar allColumnDefs para que siempre incluya todas las columnas disponibles
  const allColumns = useMemo(() => {
    return allColumnDefs
      .filter((col) => col.field && col.field !== 'actions')
      .map((col) => ({
        id: col.field || '',
        accessorKey: col.field || '',
        header: col.headerName || '',
      }));
  }, [allColumnDefs]);

  const renderIncidents = () => {
    if (isMobile) {
      return (
        <IncidentsCardList
          {...incidentsViewProps}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          selectedColumns={selectedColumns}
          onOpenActions={handleOpenActions}
          onOpenHistory={handleOpenHistory}
          couriersMap={couriersMap}
        />
      );
    }

    return (
      <IncidentsAgGridTable
        incidents={incidentsViewProps.incidents}
        columnDefs={filteredColumnDefs}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isFetching={isFetching}
        sorting={sorting}
        onSortingChange={handleSortingChange}
      />
    );
  };

  if (isInitialLoad) {
    return <LoadingState message={t('loading.message', 'Cargando incidencias...')} />;
  }

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              allColumns={allColumns}
              selectedColumns={selectedColumns}
              onColumnsChange={handleColumnsChange}
              fixedColumns={fixedColumns}
              searchTerm={searchTerm}
              filterConditions={filterConditions}
              shopIds={shopIds}
              teamIds={teamIds}
              courierIds={courierIds}
              onShopChange={handleShopIdsChange}
              onTeamChange={handleTeamIdsChange}
              onCourierChange={handleCourierChange}
              onClearAll={handleClearAll}
            />
          </div>
          <TotalResults total={total} />
        </div>

        {!isLoading && (
          <IncidentStatusToolbar
            activeStatus={activeStatusTab}
            onStatusChange={handleStatusTabChange}
            statusCounts={statusCounts}
            isLoadingCounts={isLoadingCounts}
          />
        )}
      </div>

      {/* 🎯 SOLUCIÓN: Siempre renderizar AgGrid, pero con opacity 0 cuando no hay datos */}
      {!isInitialLoad && (
        <div className="relative mb-4 mt-4">
          {/* AgGrid siempre renderizado, con opacity 0 si no hay datos (márgenes como /orders) */}
          <div
            className={`transition-opacity duration-300 ${
              isFetching && !isFetchingNextPage
                ? 'opacity-50 pointer-events-none'
                : total > 0
                  ? 'opacity-100'
                  : 'opacity-0'
            }`}
          >
            {renderIncidents()}
          </div>

          {/* EmptyView solo cuando no hay datos */}
          {total === 0 && (
            <div className="absolute inset-0">
              <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.RULES} />
            </div>
          )}
        </div>
      )}

      {/* Modales */}
      <ActionsModal ref={actionsModalRef} onSuccess={handleSuccess} />
      <HistoryModal ref={historyModalRef} />
    </div>
  );
};
